import { RefObject, useEffect, useRef } from "react";

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal sheet focus: moves focus into the sheet when it opens, keeps Tab inside it,
 * closes on Escape, and returns focus to whatever opened it.
 */
export const useSheetFocus = (open: boolean, sheetRef: RefObject<HTMLElement>, onClose: () => void) => {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement as HTMLElement | null;
    const focusables = () =>
      Array.from(sheetRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) || []).filter((el) => !el.hidden);
    (focusables()[0] || sheetRef.current)?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      const list = focusables();
      if (!list.length) {
        e.preventDefault();
        sheetRef.current?.focus();
        return;
      }
      const first = list[0];
      const last = list[list.length - 1];
      const inside = sheetRef.current?.contains(document.activeElement);
      if (e.shiftKey && (document.activeElement === first || !inside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !inside)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (opener && document.contains(opener)) opener.focus();
    };
  }, [open, sheetRef]);
};
